import { createSlice, createAction } from '@reduxjs/toolkit';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { get, set, isEmpty } from 'lodash';
import {
	getIndexViewPixel,
	getStaticViewPixel,
	getBookingViewPixel,
	getPaidLandingViewPixel,
	getPSPLandingViewPixel,
	getErrorViewPixel,
	getUnitViewPixel,
	getSearchViewPixel,
	getSearchLandingViewPixel,
	getFacilityViewPixel,
	getHiddenFacilityViewPixel,
	getLandingViewPixel,
} from 'utils/tracking';
import { isFacilityInactive } from 'utils/facility';
import { PAGES } from 'store/pages/slice';
import { SEARCH } from 'store/search/actions';
import { FACILITIES } from 'store/facilities/actions';
// TODO: move
import { isInactiveFacilityError } from 'store/facilities/slice';
import { API } from 'store/middleware/api';

// SGP (Storage Guide) pages use /storage-unit-size-guide/{keyword}.html;
// PSP pages use /{type}-storage.html
const SGP_KEYWORDS_SIMPLE = ['mini-storage', 'storage-lockers'];
const SGP_KEYWORDS_STORAGE_UNIT = [
	'5x5', '5x10', '5x15', '10x10', '10x15', '10x20', '10x25', '10x30'
];

function getPSPCanonicalPath(storageType, payloadCanonical) {
	if (payloadCanonical) {
		return payloadCanonical.startsWith('/') ? payloadCanonical.slice(1) : payloadCanonical;
	}
	if (storageType === 'electricity') {
		return 'storage-with-electricity.html';
	}
	if (storageType === 'self') {
		return 'storage-101.html';
	}
	// SGP pages: storage unit size guide URLs
	if (SGP_KEYWORDS_SIMPLE.includes(storageType)) {
		return `storage-unit-size-guide/${storageType}.html`;
	}
	if (SGP_KEYWORDS_STORAGE_UNIT.includes(storageType)) {
		return `storage-unit-size-guide/${storageType}-storage-unit.html`;
	}
	// PSP pages: e.g. car-storage.html, boat-storage.html
	return `${storageType}-storage.html`;
}

export const PAGE_META = {
	SERVER_RENDER_ERROR: 'SSDC/PAGE_META/SERVER_RENDER_ERROR',
	SET: 'SFDC/PAGE_META/SET',
	SET_PAGE_TYPE: 'SFDC/PAGE_META/SET_PAGE_TYPE',
	SET_PHONE: 'SFDC/PAGE_META/SET_PHONE',
	SET_TAGS: 'SFDC/PAGE_META/SET_TAGS',
	GET_LOCAL_NUMBER_BY_ZIPCODE_REQUEST: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_ZIPCODE_REQUEST',
	GET_LOCAL_NUMBER_BY_ZIPCODE_SUCCESS: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_ZIPCODE_SUCCESS',
	GET_LOCAL_NUMBER_BY_ZIPCODE_FAILURE: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_ZIPCODE_FAILURE',
	GET_LOCAL_NUMBER_BY_CITYSTATE_REQUEST: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_CITYSTATE_REQUEST',
	GET_LOCAL_NUMBER_BY_CITYSTATE_SUCCESS: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_CITYSTATE_SUCCESS',
	GET_LOCAL_NUMBER_BY_CITYSTATE_FAILURE: 'SFDC/PAGE_META/GET_LOCAL_NUMBER_BY_CITYSTATE_FAILURE',
	GET_TOLLFREE_NUMBER_REQUEST: 'SFDC/PAGE_META/GET_TOLLFREE_NUMBER_REQUEST',
	GET_TOLLFREE_NUMBER_SUCCESS: 'SFDC/PAGE_META/GET_TOLLFREE_NUMBER_SUCCESS',
	GET_TOLLFREE_NUMBER_FAILURE: 'SFDC/PAGE_META/GET_TOLLFREE_NUMBER_FAILURE'
};

export const setPageMeta = createAction(PAGE_META.SET, (pageMeta) => ({ payload: { pageMeta } }));
export const setPageType = createAction(PAGE_META.SET_PAGE_TYPE, (pageType) => ({ payload: { pageType } }));
export const setPhone = createAction(PAGE_META.SET_PHONE, (phone) => ({ payload: { phone } }));
export const setTags = createAction(PAGE_META.SET_TAGS, (tags, noindex = false) => ({ payload: { tags, noindex } }));

export const initialState = {
	phone: {},
	pageType: null,
	statusCode: 200,
	tags: {},
	pixelData: {},
	isLandingPage: false,
	noindex: false,
	redirectURL: null,
};

export function fourOhFourPageMeta(state) {
	return {
		...state,
		pageType: 'errorPage404',
		statusCode: 404,
		tags: { title: 'Something bad happened | SpareFoot' },
		pixelData: {
			pagePixelSrc: getErrorViewPixel('error404'),
		},
	};
}

// Tells the SSR to redirect instead of rendering the app
export function redirectPageMeta(state, redirectURL = '/') {
	return {
		...state,
		statusCode: 301,
		redirectURL,
	};
}

const PSP_COMBINED_TYPES = [
	PAGES.CAR_PSP_PAGE_SUCCESS,
	PAGES.RV_PSP_PAGE_SUCCESS,
	PAGES.BOAT_PSP_PAGE_SUCCESS,
	PAGES.STORAGE_PAGE_SUCCESS,
];

const pageMetaSlice = createSlice({
	name: 'pageMeta',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(PAGE_META.SERVER_RENDER_ERROR, (state, action) => ({
				...state,
				pageType: 'errorPage500',
				statusCode: 500,
				tags: { title: 'Something bad happened | SpareFoot' },
				error: action.error,
				pixelData: {
					pagePixelSrc: getErrorViewPixel('error500'),
				},
			}))
			.addCase(PAGE_META.SET, (state, action) => {
				let metaDescription = get(
					action,
					'payload.pageMeta.tags.meta.structuredData.description'
				);
				if (metaDescription) {
					// Remove non-language characters
					metaDescription = metaDescription.replace(
						/[^a-zA-Z0-9\t\n]/g,
						' '
					);
					set(
						action,
						'payload.pageMeta.tags.meta.structuredData.description',
						metaDescription
					);
				}
				return {
					...state,
					...action.payload.pageMeta,
				};
			})
			.addCase(PAGE_META.SET_PAGE_TYPE, (state, action) => ({
				...state,
				pageType: action.payload.pageType,
			}))
			.addCase(PAGE_META.SET_PHONE, (state, action) => ({
				...state,
				phone: action.payload.phone,
			}))
			.addCase(PAGE_META.SET_TAGS, (state, action) => ({
				...state,
				tags: action.payload.tags,
				noindex: action.payload.noindex,
			}))
			.addCase(PAGES.HOMEPAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.HOME,
				pixelData: {
					pagePixelSrc: getIndexViewPixel(),
				},
			}))
			.addCase(PAGES.UNSUBSCRIBE_PAGE_SUCCESS, (state, action) => ({
				...state,
				noindex: true,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.UNSUBSCRIBE,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(PAGES.BOOKING_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.result.phone,
				pageType: PAGE_TYPES.CONFIRMATION,
				pixelData: {
					pagePixelSrc: getBookingViewPixel(
						action.payload.result.booking
					),
				},
			}))
			.addCase(PAGES.BOOKING_PAGE_FAILURE, (state, action) => {
				if ([401, 403].includes(action.error?.status)) {
					return {
						...state,
						statusCode: action.error.status,
						booking: action.error.data || {},
						isAuthorized: false,
					};
				}
				return {
					...state,
					statusCode: action.error.status || 404,
				};
			})
			.addCase(PAGES.MOVE_IN_CONFIRM_STATUS_PAGE_SUCCESS, (state, action) => ({
				...state,
				noindex: true,
				phone: action.payload.result.phone,
				pageType: action.payload.result.pageType,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(PAGES.MOVE_IN_CONFIRM_STATUS_PAGE_FAILURE, (state) =>
				fourOhFourPageMeta(state))
			.addCase(PAGES.LEGAL_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.LEGAL,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(PAGES.REBATE_CLAIM_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.REBATE,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(PAGES.REBATE_STATUS_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.REBATE_STATUS,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(PAGES.MOVING_SUPPLIES_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.MOVING_SUPPLIES_PSP,
				tags: {
					...state.tags,
					title: 'Find Moving Supplies & Moving Boxes | SpareFoot',
					meta: {
						...state.tags.meta,
						description:
							'Make your move easier with exclusive deals on boxes and other moving supplies including mattress covers, moving blankets, bubble wrap and tape.',
					},
					links: [
						...(state.tags.links || []),
						{
							rel: 'canonical',
							content: `${action.meta.baseURL}/moving-supplies.html`,
						},
					],
				},
				pixelData: {
					pagePixelSrc: getPSPLandingViewPixel('supplies'),
				},
			}))
			.addCase(PAGES.PORTABLE_STORAGE_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.PORTABLE_STORAGE_PSP,
				tags: {
					...state.tags,
					links: [
						...(state.tags.links || []),
						{
							rel: 'canonical',
							href: `${action.meta.baseURL}/portable-storage.html`,
						},
					],
				},
				pixelData: {
					pagePixelSrc: getPSPLandingViewPixel('portableStorage'),
				},
			}))
			.addCase(PAGES.PORTABLE_STORAGE_CLP_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.phone,
				pageType: PAGE_TYPES.PORTABLE_STORAGE_CLP,
				tags: {
					...state.tags,
					links: [
						...(state.tags.links || []),
						{
							rel: 'canonical',
							content: `${action.meta.baseURL}${action.payload.canonicalURL}`,
						},
					],
				},
				pixelData: {
					pagePixelSrc: getLandingViewPixel({
						content: action.payload.content,
					}),
				},
			}))
			.addCase(PAGES.PORTABLE_STORAGE_CLP_FAILURE, (state) =>
				fourOhFourPageMeta(state))
			.addCase(PAGES.UNIT_PAGE_SUCCESS, (state, action) => {
				const result = get(action, 'payload.result');
				return {
					...state,
					phone: get(result, 'phone'),
					pageType: PAGE_TYPES.RESERVE,
					pixelData: {
						pagePixelSrc: getUnitViewPixel(
							get(result, 'unit'),
							get(result, 'facility')
						),
					},
				};
			})
			.addCase(PAGES.UNIT_PAGE_FAILURE, (state) => fourOhFourPageMeta(state))
			.addCase(PAGES.PAID_LANDING_TEMPLATE_SUCCESS, (state, action) => {
				return {
					...state,
					storageType: action.meta.storageType,
					location: action.meta.location,
					pageType: `PaidLanding-${action.meta.storageType}`,
					pixelData: getPaidLandingViewPixel(action.meta.storageType),
					noindex: true,
					tags: {
						...state.tags,
						title: `Self-Storage | SpareFoot`,
						meta: {
							...state.tags.meta,
							description: `Find cheap storage units ${
								action.meta.location
									? `in ${action.meta.location}.`
									: 'near you.'
							} Compare local storage facilities for the best price and reserve for free online.`,
						},
						links: [
							...(state.tags.links || []),
							{
								rel: 'canonical',
								content: `${action.meta.baseURL}${action.meta.url}`,
							},
						],
					},
				};
			})
			.addCase(PAGES.PAID_LANDING_TEMPLATE_FAILURE, (state) =>
				fourOhFourPageMeta(state))
			.addCase(SEARCH.PAGE_SUCCESS, (state, { payload }) => ({
				...state,
				phone: payload.result.phone,
				noindex: get(payload, 'result.meta.noindex', true),
				pageType: PAGE_TYPES.SEARCH,
				pixelData: {
					pagePixelSrc: getSearchViewPixel(
						payload.entities.search[payload.result.search]
					),
				},
			}))
			.addCase(SEARCH.SEARCH_LANDING_SUCCESS, (state, { payload }) => {
				const alternateCity = get(
					payload,
					'result.content.lowPerformingAlt.alternateCity',
					null
				);
				const alternateState = get(
					payload,
					'result.content.lowPerformingAlt.alternateState',
					null
				);
				if (!!alternateCity && !!alternateState) {
					return redirectPageMeta(
						state,
						`/${alternateCity}-${alternateState}-self-storage.html`
					);
				}

				return {
					...state,
					phone: payload.result.phone,
					pageType: payload.result.location.subPageType,
					pixelData: {
						pagePixelSrc: getSearchLandingViewPixel(payload),
					},
				};
			})
			.addCase(SEARCH.SUCCESS, (state) => {
				return state;
			})
			.addCase(SEARCH.PAGE_FAILURE, (state, { error }) => ({
				...state,
				pixelData: {
					pagePixelSrc: getSearchViewPixel(),
				},
				statusCode:
					error && (error.status === 410 || error.status === 404)
						? error.status
						: state.statusCode,
			}))
			.addCase(SEARCH.SEARCH_LANDING_FAILURE, (state, { error }) => ({
				...state,
				statusCode:
					error && (error.status === 410 || error.status === 404)
						? error.status
						: state.statusCode,
			}))
			.addCase(PAGES.REVIEW_PAGE_SUCCESS, (state, { payload }) => ({
				...state,
				noindex: true,
				phone: payload.phone,
				pageType: PAGE_TYPES.REVIEW,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(FACILITIES.FACILITY_PAGE_SUCCESS, (state, { payload, meta = {} }) => {
				if (!get(payload, 'result.facility')) {
					return fourOhFourPageMeta(state);
				}

				const facility =
					payload.entities.facilities[payload.result.facility];
				const { city, state: facilityState } = meta;
				if (city && facilityState) {
					const cityState = `${city}-${facilityState}`;
					const facilityUrl = get(facility, 'url.facility');

					if (facilityUrl && !facilityUrl.includes(cityState)) {
						return redirectPageMeta(state, facilityUrl);
					}
				}

				return {
					...state,
					phone: payload.result.phone,
					pageType: facility.isOutOfNetwork
						? PAGE_TYPES.OON_FACILITY
						: PAGE_TYPES.FACILITY,
					pixelData: {
						pagePixelSrc: isFacilityInactive(facility)
							? getHiddenFacilityViewPixel(payload.result)
							: getFacilityViewPixel(payload.result),
					},
				};
			})
			.addCase(FACILITIES.FACILITY_PAGE_FAILURE, (state, { error }) => {
				const canonicalUrl = get(error, 'data.context.url.facility', null);
				if (!isEmpty(canonicalUrl)) {
					return redirectPageMeta(state, canonicalUrl);
				}

				if (isInactiveFacilityError(error)) {
					const phone = get(error, 'data.context.phone');
					return {
						...state,
						noindex: true,
						tags: {
							...state.tags,
							title: 'SpareFoot',
						},
						pixelData: {
							pagePixelSrc: getFacilityViewPixel({
								facility: error.data.context.facilityId,
							}),
						},
						phone,
					};
				}
				return fourOhFourPageMeta(state);
			})
			.addCase(PAGES.STATE_LANDING_PAGE_SUCCESS, (state, action) => {
				if (!get(action, 'payload.location.state')) {
					return fourOhFourPageMeta(state);
				}
				return {
					...state,
					phone: action.payload.phone,
					pageType: PAGE_TYPES.STATE_LANDING,
					pixelData: {
						pagePixelSrc: getLandingViewPixel({
							content: action.payload.content,
						}),
					},
				};
			})
			.addCase(PAGES.STATE_LANDING_PAGE_FAILURE, (state) => {
				return fourOhFourPageMeta(state);
			})
			.addCase(PAGES.PSP_PAGE_SUCCESS, (state, { meta, payload }) => ({
				...state,
				phone: payload.phone,
				pageType: `${meta.page}LandingPage`,
				pixelData: {
					pagePixelSrc: getPSPLandingViewPixel(meta.storageType),
				},
				tags: {
					...state.tags,
					links: [
						...(state.tags.links || []),
						{
							rel: 'canonical',
							href: `${meta.baseURL}/${getPSPCanonicalPath(
								meta.storageType,
								get(payload, 'meta.canonical')
							)}`,
						},
					],
				},
			}))
			.addCase(PAGES.MOVING_TRUCK_PAGE_SUCCESS, (state, action) => ({
				...state,
				phone: action.payload.result.phone,
				pageType: PAGE_TYPES.MOVING_TRUCK_PSP,
				pixelData: {
					pagePixelSrc: getPSPLandingViewPixel('movingTruck'),
				},
			}))
			.addCase(PAGES.MOVING_TRUCK_PAGE_FAILURE, (state) => fourOhFourPageMeta(state))
			.addCase(PAGES.MOVING_TRUCK_CLP_SUCCESS, (state, { payload }) => {
				const truckResults =
					payload.entities.search[payload.result.truckResults];
				return {
					...state,
					phone: payload.result.phone,
					pageType: PAGE_TYPES.MOVING_TRUCK_CLP,
					pixelData: {
						pagePixelSrc: getLandingViewPixel({
							result: truckResults,
							content: payload.result.content,
						}),
					},
				};
			})
			.addCase(PAGES.MOVING_TRUCK_CLP_FAILURE, (state) => fourOhFourPageMeta(state))
			.addCase(PAGES.MOVEIN_INCENTIVE_PAGE_SUCCESS, (state, action) => ({
				...state,
				tags: { title: 'SpareFoot' },
				phone: action.payload.result.phone,
				pageType: action.payload.result.pageType,
			}))
			.addCase(PAGES.VEHICLE_LENGTH_PAGE_SUCCESS, (state) => ({
				...state,
				noindex: true,
				tags: {
					title: 'SpareFoot: Vehicle Length Tool',
				},
				phone: {
					nationalSparephone: '',
					nationalSparephoneUnformatted: '',
				},
				pageType: PAGE_TYPES.VEHICLE_LENGTH,
				pixelData: {
					pagePixelSrc: getStaticViewPixel(),
				},
			}))
			.addCase(SEARCH.STORAGE_DEALS_FAILURE, (state) => fourOhFourPageMeta(state))
			.addCase(SEARCH.STORAGE_DEALS_SUCCESS, (state, { payload }) => ({
				...state,
				tags: {
					...state.tags,
					title: payload.result.meta.title,
					meta: {
						...state.tags.meta,
						description: payload.result.meta.description,
					},
				},
				pageType: PAGE_SUB_TYPES.STORAGE_DEALS_LANDING,
				pixelData: {
					pagePixelSrc: getLandingViewPixel({
						result: payload.entities.search[payload.result.search],
						content: payload.result.content,
					}),
				},
			}))
			.addMatcher(
				(action) => PSP_COMBINED_TYPES.includes(action.type),
				(state, { meta, payload }) => ({
					...state,
					phone: payload.result.phone,
					pageType: `${meta.page}LandingPage`,
					pixelData: {
						pagePixelSrc: getPSPLandingViewPixel(
							meta.storageType || 'storage'
						),
					},
					tags: {
						...state.tags,
						links: [
							{
								rel: 'canonical',
								href: meta.storageType
									? `${meta.baseURL}/${meta.storageType}-storage.html`
									: `${meta.baseURL}/storage-101.html`,
							},
						],
					},
					noindex: meta.noindex || state.noindex,
				})
			);
	},
});

export function getLocalPhoneNumberByZipCode(zipCode) {
	return {
		[API]: {
			types: [PAGE_META.GET_LOCAL_NUMBER_BY_ZIPCODE_REQUEST,
				PAGE_META.GET_LOCAL_NUMBER_BY_ZIPCODE_SUCCESS,
				PAGE_META.GET_LOCAL_NUMBER_BY_ZIPCODE_FAILURE],
			endpoint: `/phone/numbers/local/zipCodes/${zipCode}`,
			method: 'GET'
		}
	};
}

export function getLocalPhoneNumberByCityState(city, state) {
	return {
		[API]: {
			types: [PAGE_META.GET_LOCAL_NUMBER_BY_CITYSTATE_REQUEST,
				PAGE_META.GET_LOCAL_NUMBER_BY_CITYSTATE_SUCCESS,
				PAGE_META.GET_LOCAL_NUMBER_BY_CITYSTATE_FAILURE],
			endpoint: `/phone/numbers/local/city/${city}/state/${state}`,
			method: 'GET'
		}
	};
}

export function getTollfreePhoneNumber() {
	return {
		[API]: {
			types: [PAGE_META.GET_TOLLFREE_NUMBER_REQUEST,
				PAGE_META.GET_TOLLFREE_NUMBER_SUCCESS,
				PAGE_META.GET_TOLLFREE_NUMBER_FAILURE],
			endpoint: '/phone/numbers/tollfree',
			method: 'GET'
		}
	};
}

export function serverRenderError(error) {
	return {
		type: PAGE_META.SERVER_RENDER_ERROR,
		error
	};
}

export default pageMetaSlice.reducer;
